import { useNavigate, useSearch } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { BrandLockup } from '../components/Brand.tsx';
import { EASE } from '../components/motion.tsx';
import { Button, Callout, Field, useToast } from '../components/ui.tsx';
import { ApiError, post } from '../lib/api.ts';
import { time } from '../lib/format.ts';
import { noteSignIn } from '../lib/push.ts';
import { useSetMe, type Me } from '../lib/session.ts';

function Clock({ tz, label, abbr }: { tz: string; label: string; abbr: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span className="eyebrow">{label}</span>
      <span className="display num" style={{ fontSize: 'var(--step-3)' }}>
        {time(now, tz)}
        <span className="faint" style={{ fontSize: 12, fontWeight: 500, marginLeft: 8 }}>
          {abbr}
        </span>
      </span>
    </div>
  );
}

export function AuthCanvas({ children, statement }: { children: ReactNode; statement?: ReactNode }) {
  return (
    <div className="auth">
      <section className="auth__canvas" aria-hidden="true">
        <BrandLockup className="auth__lockup" ink="#ffffff" />
        <motion.p className="auth__quote" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
          {statement ?? (
            <>
              The network is <em>people.</em> We keep their record honest.
            </>
          )}
        </motion.p>
        <div className="row row--wrap" style={{ ['--gap' as string]: '40px', alignItems: 'end' }}>
          <Clock tz="Asia/Makassar" label="Denpasar" abbr="WITA" />
          <Clock tz="Asia/Jakarta" label="Malang" abbr="WIB" />
          <span className="eyebrow" style={{ marginLeft: 'auto' }}>
            One sign-in for every GX app
          </span>
        </div>
      </section>
      <section className="auth__form">{children}</section>
    </div>
  );
}

export function Login() {
  const search = useSearch({ strict: false }) as { next?: string };
  const navigate = useNavigate();
  const setMe = useSetMe();
  const [mode, setMode] = useState<'login' | 'forgot' | 'sent'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await post('/api/auth/password/forgot', { email });
        setMode('sent');
      } else {
        const me = await post<Me>('/api/auth/login', { email, password });
        noteSignIn();
        setMe(me);
        const next = search.next && search.next.startsWith('/') && !search.next.startsWith('//') ? search.next : '/';
        navigate({ to: next });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We couldn’t reach Hearth. Check your connection.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCanvas>
      <motion.form className="auth__form-inner" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, ease: EASE }} noValidate>
        <div className="stack" style={{ ['--gap' as string]: '8px' }}>
          <span className="eyebrow">{mode === 'login' ? 'Sign in' : 'Reset password'}</span>
          <h1 className="display" style={{ fontSize: 'var(--step-3)' }}>
            {mode === 'login' ? (
              <>
                Welcome <em>back.</em>
              </>
            ) : mode === 'forgot' ? (
              <>
                Forgot it? <em>Happens.</em>
              </>
            ) : (
              <>
                Check your <em>inbox.</em>
              </>
            )}
          </h1>
          <p className="muted">
            {mode === 'login'
              ? 'Use your GlobalXtreme work email.'
              : mode === 'forgot'
                ? 'We’ll email a link that works for 30 minutes.'
                : `If ${email} has an account, a reset link is on its way.`}
          </p>
        </div>
        {error ? <Callout tone="bad">{error}</Callout> : null}
        {mode !== 'sent' ? (
          <>
            <Field label="Work email">{(p) => <input {...p} className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nama@globalxtreme.net" />}</Field>
            {mode === 'login' ? (
              <Field
                label="Password"
                aside={
                  <button type="button" className="link faint" style={{ border: 0, background: 'none', cursor: 'pointer', fontSize: 'var(--step--2)' }} onClick={() => setMode('forgot')}>
                    Forgot?
                  </button>
                }
              >
                {(p) => <input {...p} className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />}
              </Field>
            ) : null}
            <Button variant="primary" size="lg" type="submit" loading={busy} iconRight="arrowRight">
              {mode === 'login' ? 'Sign in' : 'Send reset link'}
            </Button>
          </>
        ) : null}
        {mode !== 'login' ? (
          <Button variant="ghost" type="button" icon="chevronLeft" onClick={() => setMode('login')}>
            Back to sign in
          </Button>
        ) : null}
        <p className="faint" style={{ fontSize: 'var(--step--2)' }}>
          Signing in to CRM, Inventory or Ticket? They send you here; you’ll be returned automatically.
        </p>
      </motion.form>
    </AuthCanvas>
  );
}

export function ResetPassword() {
  const { token } = useSearch({ strict: false }) as { token: string };
  const navigate = useNavigate();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) return setError('The two passwords don’t match.');
    setBusy(true);
    setError(null);
    try {
      await post('/api/auth/password/reset', { token, password });
      toast({ title: 'Password saved', body: 'Sign in with your new password.' });
      navigate({ to: '/login' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthCanvas statement={<>A fresh start, <em>quietly.</em></>}>
      <form className="auth__form-inner" onSubmit={submit}>
        <span className="eyebrow">New password</span>
        <h1 className="display" style={{ fontSize: 'var(--step-3)' }}>
          Choose a <em>new one.</em>
        </h1>
        {error ? <Callout tone="bad">{error}</Callout> : null}
        <Field label="New password" hint="At least 10 characters. A short sentence works well.">
          {(p) => <input {...p} className="input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
        </Field>
        <Field label="Confirm">{(p) => <input {...p} className="input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}</Field>
        <Button variant="primary" size="lg" type="submit" loading={busy}>
          Save password
        </Button>
      </form>
    </AuthCanvas>
  );
}
